import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { ACCOUNT_TYPES } from 'constants/common';
import { buildCallDashboardPath, ROOT_PATH } from 'constants/route-paths';
import authActions from 'actions/auth-actions';
import implicitAuthActions from 'actions/implicit-auth-actions';
import { authTokenSelector, isSuccessfulSignInSelector, signedInUserSelector } from 'selectors/auth';
import { userOriginalSiteSelector } from 'selectors/current-user';
import { siteFromRootSiteSelector } from 'selectors/root-site';

export const useSignedInUser = () => {
  const signedInUser = useSelector(signedInUserSelector);

  return isEmpty(signedInUser)
    ? {}
    : {
      ...signedInUser,
      accountType: signedInUser.isExpert
        ? ACCOUNT_TYPES.expert
        : signedInUser.isMember
          ? ACCOUNT_TYPES.learner
          : ACCOUNT_TYPES.visitor,
      suspendedExpert: signedInUser.isExpert && signedInUser.expertStatus === 'suspended',
    };
};

export const useAuthenticated = () => useSelector(authTokenSelector);

export const useHandleSignIn = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const locationFromState = get(useLocation(), 'state.from');
  const isAuthenticated = useAuthenticated();
  const { siteSlug } = useParams();

  const userOriginalSite = useSelector(userOriginalSiteSelector);
  const isSuccessfulSignIn = useSelector(isSuccessfulSignInSelector);

  useEffect(() => {
    if (isAuthenticated && isSuccessfulSignIn) {
      if (locationFromState) {
        history.replace(locationFromState);
      } else if (siteSlug) {
        history.replace(buildCallDashboardPath(siteSlug));
      } else {
        history.replace(ROOT_PATH);
      }

      userOriginalSite.rootDomain !== window.location.hostname &&
        dispatch(authActions.showConfirmNavigationModal(true));

      dispatch(authActions.clearSubmitedSignedInSignal());
    }
  }, [
    dispatch,
    history,
    isAuthenticated,
    isSuccessfulSignIn,
    locationFromState,
    siteSlug,
    userOriginalSite.rootDomain,
  ]);
};

export const useDetectingAuthenticatedUser = (visible) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(implicitAuthActions.observeDetectingAuthenticatedUser(visible));

    return () => {
      dispatch(implicitAuthActions.observeDetectingAuthenticatedUser(false));
    };
  });
};

export const useIsEmailWhitelisted = (email) => {
  const { siteSlug } = useParams();

  const { whitelistDomains } = useSelector(siteFromRootSiteSelector(siteSlug));

  return useMemo(() => {
    if (isEmpty(email) || isEmpty(whitelistDomains)) return false;

    const emailDomain = email.split('@').pop();

    return whitelistDomains.some((whitelisted) =>
      whitelisted.startsWith('.') ? emailDomain.endsWith(whitelisted) : emailDomain === whitelisted,
    );
  }, [email, whitelistDomains]);
};
